import { useState } from 'react';
import { residentConditionAck, residentSbar, stopAndWatch, stopAndWatchFeedback, type ApiError, type StopAndWatchResponse } from '@aithora/shared';
import { EmptyState, ErrorState, Skeleton } from '../../ui/states';
import { type ResidentsCtx, useApi } from './common';
import { alertsVals, type AlertsUi } from './plansVals';
import { AlertsView } from './AlertsView';
import { facilitiesParam, usePickedIds } from '../../session/FacilityScope';

/** Alerts (design rIsAlerts, T3226): Stop and Watch, worded as observations, never diagnoses. */
export function AlertsTab({ ctx }: { ctx: ResidentsCtx }) {
  const pick = facilitiesParam(usePickedIds());
  const q = useApi<StopAndWatchResponse>((c, signal) => stopAndWatch(c, { query: pick ? { facilities: pick } : undefined, signal }), [pick]);
  const [ui, setUiState] = useState<AlertsUi>({ sel: null, filter: null, evOpen: true, sbarFor: null, acked: {}, sent: {} });
  const [err, setErr] = useState<ApiError | null>(null);
  const setUi = (p: Partial<AlertsUi>) => setUiState((s) => ({ ...s, ...p }));
  if (q.loading && !q.data && !q.error) return <Skeleton rows={6} label="Loading Stop and Watch" />;
  if (!q.data) {
    return q.error?.status === 404 ? (
      <EmptyState title="Stop and Watch moves here when the back end lists it" body="Each observation with the evidence under it and a pre-filled SBAR." waitingOn="GET /residents/stop-and-watch" />
    ) : q.error ? <ErrorState error={q.error} onRetry={q.reload} /> : null;
  }
  const fail = (e: ApiError) => setErr(e);
  const V = alertsVals({
    alerts: q.data.alerts,
    ui,
    setUi,
    // Aithora's team and the Partner read every building and act in none (the server
    // refuses them), so they get no Acknowledge or Not useful (QA-11).
    canWrite: ctx.canAct,
    roleLabel: ctx.roleLabel,
    ack: (a) => {
      setErr(null);
      residentConditionAck(ctx.client, { params: { id: a.residentId }, body: {} }).then(() => setUi({ acked: { ...ui.acked, [a.residentId]: true } })).catch(fail);
    },
    send: (a, sbar) => {
      setErr(null);
      const v = (k: string) => sbar.find((s) => s.k === k)?.v ?? '';
      residentSbar(ctx.client, { params: { id: a.residentId }, body: { situation: v('S'), background: v('B'), assessment: v('A'), recommendation: v('R') } })
        .then(() => setUi({ sent: { ...ui.sent, [a.residentId]: true } }))
        .catch(fail);
    },
    notUseful: (a) => { stopAndWatchFeedback(ctx.client, { params: { id: a.residentId }, body: { useful: false } }).catch(fail); },
  });
  return (
    <>
      <AlertsView V={{ ...V, rIsAlerts: true }} />
      {err && <ErrorState error={err} />}
    </>
  );
}
